import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SchedulingAsync as Async,
  isSlotTaken,
  problemMessage,
  shiftDate,
} from '@/features/scheduling';
import { WaitlistJoin } from '@/features/booking-extras';
import type { Booking } from '@/features/scheduling';

import type { ClientBookingItem } from './api';
import {
  AddonChips,
  ConsentCheckbox,
  DateStrip,
  OtpInput,
  PhoneInput,
  PolicyNote,
  ServiceGrid,
  SlotChips,
  Summary,
  TechnicianPicker,
  addonsFor,
  basketTotals,
  dateStrip,
} from './components';
import { useBookingDeps } from './context';
import { toggle, useJourney } from './journey';
import { toE164 } from './phone';

/**
 * §6's five booking steps, each its own screen at 390 px.
 *
 * Every step reads and writes the one draft (`useJourney`) and nothing else; the "next"
 * button is the only thing that knows where the journey goes, and the app shell tells it.
 */

/** The query keys the journey caches under. */
export const BOOKING_KEYS = {
  services: ['booking', 'services'] as const,
  addons: ['booking', 'addons'] as const,
  staff: ['booking', 'staff'] as const,
  slots: (date: string, serviceIds: string[], staffId: string | null, addonIds: string[]) =>
    ['booking', 'slots', date, serviceIds, staffId, addonIds] as const,
  mine: ['booking', 'mine'] as const,
  one: (id: string) => ['booking', 'one', id] as const,
  publicBooking: (ref: string, token: string) => ['booking', 'public', ref, token] as const,
};

/** How many days §6's date strip offers at once. */
export const STRIP_DAYS = 7;

/** How long the resend timer runs when the api does not say (R14's default). */
export const DEFAULT_RESEND_S = 60;

/** §6 `/book/services`. */
export function ServicesStep({ onNext }: { onNext: () => void }): JSX.Element {
  const { t } = useTranslation('booking');
  const { api } = useBookingDeps();
  const { draft, patch } = useJourney();
  const services = useQuery({ queryKey: BOOKING_KEYS.services, queryFn: () => api.listServices() });
  const addons = useQuery({ queryKey: BOOKING_KEYS.addons, queryFn: () => api.listAddons() });

  const chosenServices = (services.data ?? []).filter((service) =>
    draft.serviceIds.includes(service.id),
  );
  const chosenAddons = (addons.data ?? []).filter((addon) => draft.addonIds.includes(addon.id));
  const totals = basketTotals(chosenServices, chosenAddons);

  return (
    <section data-testid="services-step" className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{t('services.title')}</h2>
      <Async pending={services.isPending} error={services.error} data={services.data}>
        {(list) => (
          <ServiceGrid
            services={list}
            chosen={draft.serviceIds}
            onToggle={(id) => {
              patch({ serviceIds: toggle(draft.serviceIds, id), slot: null });
            }}
          />
        )}
      </Async>
      <Async pending={addons.isPending} error={addons.error} data={addons.data}>
        {(list) => (
          <AddonChips
            addons={addonsFor(list, draft.serviceIds)}
            chosen={draft.addonIds}
            onToggle={(id) => {
              patch({ addonIds: toggle(draft.addonIds, id), slot: null });
            }}
          />
        )}
      </Async>
      <p data-testid="running-total">
        {t('services.running', {
          minutes: totals.minutes,
          amount: (totals.paise / 100).toFixed(2),
        })}
      </p>
      <button
        type="button"
        data-testid="services-next"
        disabled={draft.serviceIds.length === 0}
        onClick={onNext}
        className="rounded bg-primary px-4 py-3 text-primary-foreground disabled:opacity-40"
      >
        {t('services.next')}
      </button>
    </section>
  );
}

/** §6 `/book/technician`. */
export function TechnicianStep({ onNext }: { onNext: () => void }): JSX.Element {
  const { t } = useTranslation('booking');
  const { api } = useBookingDeps();
  const { draft, patch } = useJourney();
  const roster = useQuery({ queryKey: BOOKING_KEYS.staff, queryFn: () => api.listStaff() });

  return (
    <section data-testid="technician-step" className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{t('technician.title')}</h2>
      <Async pending={roster.isPending} error={roster.error} data={roster.data}>
        {(rows) => (
          <TechnicianPicker
            staff={rows}
            chosen={draft.staffId}
            onChoose={(id) => {
              patch({ staffId: id, slot: null });
            }}
          />
        )}
      </Async>
      <button
        type="button"
        data-testid="technician-next"
        onClick={onNext}
        className="rounded bg-primary px-4 py-3 text-primary-foreground"
      >
        {t('technician.next')}
      </button>
    </section>
  );
}

/** §6 `/book/slot`. */
export function SlotStep({ onNext }: { onNext: () => void }): JSX.Element {
  const { t } = useTranslation('booking');
  const { api, timezone } = useBookingDeps();
  const { draft, patch } = useJourney();
  const days = dateStrip(draft.date, STRIP_DAYS);

  const slots = useQuery({
    queryKey: BOOKING_KEYS.slots(draft.date, draft.serviceIds, draft.staffId, draft.addonIds),
    queryFn: () =>
      api.availability({
        date: draft.date,
        service_ids: draft.serviceIds,
        ...(draft.staffId === null ? {} : { staff_id: draft.staffId }),
        ...(draft.addonIds.length === 0 ? {} : { addon_ids: draft.addonIds }),
      }),
  });

  return (
    <section data-testid="slot-step" className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{t('slot.title')}</h2>
      <DateStrip
        days={days}
        chosen={draft.date}
        timezone={timezone}
        onChoose={(date) => {
          patch({ date, slot: null });
        }}
      />
      <Async pending={slots.isPending} error={slots.error} data={slots.data}>
        {(value) => (
          <>
            <SlotChips
              slots={value.slots}
              timezone={timezone}
              chosen={draft.slot}
              nextFree={shiftDate(draft.date, 1)}
              onChoose={(slot) => {
                patch({ slot });
              }}
            />
            {/* BP-25 §6: a day with nothing free is the one moment a
                waitlist is worth offering. Her window is the day she
                asked for, which is what the strip above already says. */}
            {value.slots.length === 0 ? (
              <WaitlistJoin
                serviceIds={draft.serviceIds}
                windowFrom={`${draft.date}T00:00:00+05:30`}
                windowTo={`${draft.date}T23:59:00+05:30`}
              />
            ) : null}
          </>
        )}
      </Async>
      <button
        type="button"
        data-testid="slot-next"
        disabled={draft.slot === null}
        onClick={onNext}
        className="rounded bg-primary px-4 py-3 text-primary-foreground disabled:opacity-40"
      >
        {t('slot.next')}
      </button>
    </section>
  );
}

/** §6 `/book/verify`: phone, OTP, the reminders tick and BP-03's notice link. */
export function VerifyStep({ onVerified }: { onVerified: () => void }): JSX.Element {
  const { t } = useTranslation('booking');
  const { api, noticeHref } = useBookingDeps();
  const { draft, patch } = useJourney();
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [resendAfter, setResendAfter] = useState(DEFAULT_RESEND_S);
  const [error, setError] = useState<unknown>(null);
  //: The number as the api takes it, or `''` while what was typed cannot be one. The
  //: buttons are disabled on the empty string, so no guard inside the handlers is needed.
  const e164 = toE164(draft.phone) ?? '';

  const send = (): void => {
    setError(null);
    api
      .requestOtp({ phone_e164: e164, consent_reminders: draft.consentReminders })
      .then((answer) => {
        setSent(true);
        setResendAfter(answer.resend_after_s);
      })
      .catch((reason: unknown) => {
        setError(reason);
      });
  };

  const verify = (): void => {
    setError(null);
    api
      .verifyOtp({ phone_e164: e164, code })
      .then((answer) => {
        patch({ clientId: answer.client_id });
        onVerified();
      })
      .catch((reason: unknown) => {
        setError(reason);
      });
  };

  return (
    <section data-testid="verify-step" className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{t('verify.title')}</h2>
      <PhoneInput
        value={draft.phone}
        onChange={(next) => {
          patch({ phone: next });
        }}
      />
      <ConsentCheckbox
        checked={draft.consentReminders}
        noticeHref={noticeHref}
        onChange={(next) => {
          patch({ consentReminders: next });
        }}
      />
      {sent ? (
        <>
          <OtpInput value={code} length={6} onChange={setCode} />
          <button
            type="button"
            data-testid="otp-verify"
            disabled={code.length < 4}
            onClick={verify}
            className="rounded bg-primary px-4 py-3 text-primary-foreground disabled:opacity-40"
          >
            {t('verify.verify')}
          </button>
          <p data-testid="resend-hint" className="text-sm text-muted-foreground">
            {t('verify.resendIn', { seconds: resendAfter })}
          </p>
        </>
      ) : null}
      <button
        type="button"
        data-testid="otp-send"
        disabled={e164 === ''}
        onClick={send}
        className="rounded border border-border px-4 py-3"
      >
        {sent ? t('verify.resend') : t('verify.send')}
      </button>
      {error === null ? null : (
        <p role="alert" data-testid="verify-error">
          {problemMessage(error, t('verify.invalid'))}
        </p>
      )}
    </section>
  );
}

/** The `POST /bookings` body the draft describes. */
export function bookingItems(
  serviceIds: readonly string[],
  addonIds: readonly string[],
  slot: { start_at: string; staff_id: string } | null,
  preselectedSkuId: string | null = null,
): ClientBookingItem[] {
  if (slot === null) {
    return [];
  }
  return serviceIds.map((serviceId, index) => ({
    service_id: serviceId,
    staff_id: slot.staff_id,
    start_at: slot.start_at,
    // R3 hangs the add-ons off the first item: they extend that service, not the visit.
    addon_ids: index === 0 ? [...addonIds] : [],
    // BP-21: the shade rides the first item for the same reason the add-ons
    // do. She tried one colour; it belongs to the service she tried it for,
    // not to every service in the basket.
    ...(index === 0 && preselectedSkuId !== null ? { preselected_sku_id: preselectedSkuId } : {}),
  }));
}

/**
 * BP-21 §11 item 6: a journey started from the try-on screen books as `ar`.
 *
 * The source is derived from the draft rather than passed in, so there is
 * no way to send `source: 'ar'` with no shade or a shade with `source:
 * 'web'` — the two facts come from one.
 */
export function bookingSource(preselectedSkuId: string | null): string {
  return preselectedSkuId === null ? 'web' : 'ar';
}

/** §6 `/book/confirm`. */
export function ConfirmStep({
  cutoffHours,
  onBooked,
}: {
  cutoffHours: number;
  onBooked: (booking: Booking) => void;
}): JSX.Element {
  const { t } = useTranslation('booking');
  const { api, timezone, newIdempotencyKey } = useBookingDeps();
  const { draft } = useJourney();
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const services = useQuery({ queryKey: BOOKING_KEYS.services, queryFn: () => api.listServices() });
  const addons = useQuery({ queryKey: BOOKING_KEYS.addons, queryFn: () => api.listAddons() });

  const chosenServices = (services.data ?? []).filter((service) =>
    draft.serviceIds.includes(service.id),
  );
  const chosenAddons = (addons.data ?? []).filter((addon) => draft.addonIds.includes(addon.id));

  const confirm = (): void => {
    setBusy(true);
    setError(null);
    api
      .createBooking(
        {
          items: bookingItems(draft.serviceIds, draft.addonIds, draft.slot, draft.preselectedSkuId),
          source: bookingSource(draft.preselectedSkuId),
          client_id: draft.clientId,
        },
        newIdempotencyKey(),
      )
      .then(onBooked)
      .catch((reason: unknown) => {
        setError(reason);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const slot = draft.slot;
  if (slot === null) {
    // Reachable only by opening `/book/confirm` cold; the journey itself cannot get here.
    return (
      <p role="status" data-testid="confirm-no-slot">
        {t('slot.none')}
      </p>
    );
  }

  return (
    <section data-testid="confirm-step" className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{t('confirm.title')}</h2>
      <Summary
        services={chosenServices}
        addons={chosenAddons}
        startAt={slot.start_at}
        timezone={timezone}
      />
      <PolicyNote cutoffHours={cutoffHours} />
      {error === null ? null : (
        <p role="alert" data-testid="confirm-error">
          {isSlotTaken(error) ? t('confirm.taken') : problemMessage(error, t('confirm.taken'))}
        </p>
      )}
      <button
        type="button"
        data-testid="confirm-book"
        disabled={busy}
        onClick={confirm}
        className="rounded bg-primary px-4 py-3 text-primary-foreground disabled:opacity-40"
      >
        {t('confirm.book')}
      </button>
    </section>
  );
}
